import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, Plus, Trophy, DollarSign, Users, Lock, CheckCircle2, XCircle } from "lucide-react";
import { RfpThreadPanel } from "@/components/RfpThreadPanel";

export default function AdminRfpPage() {
  const { propertyId, currentProperty } = useProperty();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", budget_ceiling: "", scope: "",
    category: "General", deadline: "", invited_vendor_ids: [],
  });

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/rfp?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [propertyId]);

  const loadVendors = useCallback(async () => {
    try {
      const { data } = await api.get(`/users?property_id=${propertyId || ""}`);
      setVendors((data.items || []).filter((u) => u.role === "vendor"));
    } catch { setVendors([]); }
  }, [propertyId]);

  useEffect(() => { load(); loadVendors(); }, [load, loadVendors]);

  // Live sync — any bid, award, or new RFP on this property lands here instantly.
  useSubscribe(propertyId ? `property:${propertyId}` : null, ({ event, payload }) => {
    if (["rfp.new", "rfp.bid.new", "rfp.awarded", "rfp.update"].includes(event)) {
      load();
    }
  });

  const toggleVendor = (id) => {
    setForm((f) => ({
      ...f,
      invited_vendor_ids: f.invited_vendor_ids.includes(id)
        ? f.invited_vendor_ids.filter((x) => x !== id)
        : [...f.invited_vendor_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.invited_vendor_ids.length === 0) return toast.error("Invite at least one vendor");
    setSaving(true);
    try {
      await api.post("/rfp", {
        ...form,
        property_id: propertyId,
        budget_ceiling: form.budget_ceiling ? Number(form.budget_ceiling) : null,
      });
      toast.success("RFP posted — invited vendors notified");
      setOpen(false);
      setForm({ title: "", description: "", budget_ceiling: "", scope: "",
                category: "General", deadline: "", invited_vendor_ids: [] });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const award = async (rfpId, bidId) => {
    try {
      await api.post(`/rfp/${rfpId}/award`, { bid_id: bidId });
      toast.success("Awarded — vendor notified");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const close = async (rfpId) => {
    if (!window.confirm("Close this RFP without awarding?")) return;
    try {
      await api.post(`/rfp/${rfpId}/close`, {});
      toast.success("RFP closed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const active = useMemo(() => items.filter((r) => r.status === "open"), [items]);
  const decided = useMemo(() => items.filter((r) => r.status !== "open"), [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Procurement"
        title="RFPs — Private Bidding"
        subtitle="Post large jobs to specific vendors. Bids are sealed until you award."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-rfp-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full">
                <Plus className="h-4 w-4 mr-1" /> New RFP
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-2xl">
              <DialogHeader><DialogTitle>New Request for Proposal · {currentProperty?.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 scroll-slim">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="rfp-title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Roof replacement — Tower B" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Category</Label>
                    <Input data-testid="rfp-category" value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })} />
                  </div>
                  <div>
                    <Label>Budget ceiling (USD)</Label>
                    <Input data-testid="rfp-budget" type="number" min="0" value={form.budget_ceiling}
                      onChange={(e) => setForm({ ...form, budget_ceiling: e.target.value })}
                      placeholder="Max you'd pay" />
                  </div>
                </div>
                <div>
                  <Label>Deadline (ISO date)</Label>
                  <Input data-testid="rfp-deadline" type="date" value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                </div>
                <div>
                  <Label>Scope of work</Label>
                  <Textarea data-testid="rfp-scope" rows={3} value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                    placeholder="What must be included in every bid" />
                </div>
                <div>
                  <Label>Description / notes</Label>
                  <Textarea data-testid="rfp-desc" rows={2} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Invite vendors</Label>
                    <div className="text-[11px] text-zinc-500">{form.invited_vendor_ids.length} selected</div>
                  </div>
                  <div className="rounded-md border border-white/10 bg-white/[0.02] p-2 max-h-48 overflow-y-auto scroll-slim">
                    {vendors.length === 0 && <div className="text-xs text-muted-foreground p-2">No vendors on file for this property.</div>}
                    {vendors.map((v) => {
                      const on = form.invited_vendor_ids.includes(v.id);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => toggleVendor(v.id)}
                          data-testid={`rfp-invite-${v.id}`}
                          className={"w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm text-left " +
                            (on ? "bg-indigo-500/10 border border-indigo-500/30" : "hover:bg-white/5 border border-transparent")}
                        >
                          <div>
                            <div className="text-sm font-medium">{v.name}</div>
                            <div className="text-[11px] text-zinc-500 truncate">{v.email}</div>
                          </div>
                          {on ? <CheckCircle2 className="h-4 w-4 text-indigo-300" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button data-testid="rfp-submit" onClick={submit} disabled={saving}
                        className="w-full bg-orange-500 hover:bg-orange-600">
                  {saving ? "Posting…" : "Post RFP"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="space-y-3">
        {active.length > 0 && <div className="overline mb-1">Open RFPs</div>}
        {active.map((r) => (
          <Card key={r.id} className="border-white/10 bg-card p-5" data-testid={`rfp-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">Open</Badge>
                  <div className="overline">{r.category}</div>
                  {r.budget_ceiling ? <div className="text-xs text-zinc-500">Budget ≤ ${Number(r.budget_ceiling).toLocaleString()}</div> : null}
                  {r.deadline ? <div className="text-xs text-zinc-500">Deadline {r.deadline}</div> : null}
                </div>
                <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-orange-400" /> {r.title}
                </div>
                {r.scope && <div className="text-sm text-zinc-400 mt-1"><b>Scope:</b> {r.scope}</div>}
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                  <Users className="h-3 w-3" /> {(r.invited_vendors || []).map((v) => v.name).join(", ") || "No invites"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-[11px] flex items-center gap-1 text-zinc-500"><Lock className="h-3 w-3" /> Sealed</div>
                <Button size="sm" variant="outline" className="border-white/10" onClick={() => close(r.id)} data-testid={`rfp-close-${r.id}`}>Close</Button>
              </div>
            </div>
            <div className="mt-4">
              <div className="overline mb-2">Sealed bids ({(r.bids || []).length})</div>
              {(r.bids || []).length === 0 ? (
                <div className="text-sm text-zinc-500">No bids yet.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {r.bids.map((b) => (
                    <div key={b.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`bid-${b.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{b.vendor_name}</div>
                          <div className="text-lg tabular-nums text-emerald-300">${Number(b.amount).toFixed(2)}</div>
                          {b.eta_days != null && <div className="text-[11px] text-zinc-500">ETA {b.eta_days} days</div>}
                        </div>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => award(r.id, b.id)} data-testid={`award-${b.id}`}>
                          <Trophy className="h-4 w-4 mr-1" /> Award
                        </Button>
                      </div>
                      {b.proposal && <div className="text-xs text-zinc-400 mt-2 whitespace-pre-line">{b.proposal}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <RfpThreadPanel rfp={r} />
          </Card>
        ))}

        {decided.length > 0 && <div className="overline mt-6 mb-1">History</div>}
        {decided.map((r) => (
          <Card key={r.id} className="border-white/10 bg-card p-4" data-testid={`rfp-${r.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <Badge variant="outline" className={
                  r.status === "awarded" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                  : "border-white/10 text-zinc-400"
                }>
                  {r.status === "awarded" ? <Trophy className="h-3 w-3 mr-1 inline" /> : <XCircle className="h-3 w-3 mr-1 inline" />}
                  {r.status}
                </Badge>
                <span className="ml-2 font-medium">{r.title}</span>
              </div>
              {r.awarded_amount != null && (
                <div className="text-sm tabular-nums text-emerald-300 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> ${Number(r.awarded_amount).toFixed(2)}
                </div>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="border-dashed border-white/10 p-10 text-center text-muted-foreground">
            No RFPs yet. Post one to invite specific vendors to bid privately.
          </Card>
        )}
      </div>
    </div>
  );
}
